import { App } from '@capacitor/app';
import { Device } from '@capacitor/device';
import { FacebookLogin } from '@capacitor-community/facebook-login';
import { MetaAttribution } from './meta-attribution';
import { getStore } from '../store';
import { postMemberSettings } from '../store/modules/app-controls';
import { selectCurrentMember } from '../store/modules/backend-service';

const FACEBOOK_APP_ID = '681535238117830';

/** Meta expects a timezone abbreviation (e.g. AEST, GMT+10) at extinfo index 7. */
const getTimezoneAbbreviation = (): string => {
  try {
    const parts = new Intl.DateTimeFormat(undefined, {
      timeZoneName: 'short',
    }).formatToParts(new Date());

    return parts.find((part) => part.type === 'timeZoneName')?.value || 'GMT';
  } catch {
    return 'GMT';
  }
};

/**
 * Meta app_data.extinfo — versioned array documented by Meta Conversions API for App Events.
 * Order is fixed by Meta: index 0 is the platform marker (i2 iOS / a2 Android),
 * index 2 the short app version, index 3 the long/build version, index 7 the
 * timezone abbreviation and index 15 the IANA timezone name.
 */
const buildExtinfo = async (): Promise<string[]> => {
  const [device, appInfo] = await Promise.all([
    Device.getInfo(),
    App.getInfo().catch(() => null),
  ]);

  const isIos = device.platform === 'ios';
  const versionCode = appInfo?.build ?? '0';
  const versionName = appInfo?.version ?? 'unknown';

  return [
    isIos ? 'i2' : 'a2',
    'com.deckee.app',
    versionName,
    String(versionCode),
    device.osVersion ?? 'unknown',
    device.model ?? 'unknown',
    navigator.language || 'en_US',
    getTimezoneAbbreviation(),
    '',
    String(window.screen?.width ?? 0),
    String(window.screen?.height ?? 0),
    String(window.devicePixelRatio ?? 1),
    '2',
    '0',
    '0',
    Intl.DateTimeFormat().resolvedOptions().timeZone || '',
  ];
};

export const persistMetaAttribution = async () => {
  // Facebook init runs before the Redux store exists on a cold start.
  const store = getStore();
  if (!store) {
    return;
  }

  const member = selectCurrentMember(store.getState());
  if (!member?.id) {
    return;
  }

  try {
    const ids = await MetaAttribution.getAttributionIds();
    if (!ids.anonId && !ids.advertisingId) {
      return;
    }

    const extinfo = await buildExtinfo();

    await store
      .dispatch(
        postMemberSettings({
          metaAttribution: {
            anonId: ids.anonId,
            advertisingId: ids.advertisingId,
            advertiserTrackingEnabled: ids.advertiserTrackingEnabled,
            applicationTrackingEnabled: true,
            extinfo,
            updatedAt: new Date().toISOString(),
          },
        }),
      )
      .unwrap();
  } catch (error) {
    // Soft-fail: missing native plugin (web) or network must not block app boot.
    // oxlint-disable-next-line no-console -- legacy code. should use centralised logging instead
    console.error('[FacebookEvents.persistMetaAttribution] Failed:', error);
  }
};

export const initialize = async () => {
  try {
    await FacebookLogin.initialize({ appId: FACEBOOK_APP_ID });

    // Log an install / first_open event (e.g. on first run)
    await FacebookLogin.logEvent({
      eventName: 'fb_mobile_activate_app',
    });

    // Best-effort: capture attribution ids as soon as FB SDK is ready.
    // Member may not be authenticated yet; persistMetaAttribution no-ops then.
    await persistMetaAttribution();
  } catch (error) {
    // Handle error, optionally log or ignore
    // oxlint-disable-next-line no-console -- legacy code. should use centralised logging instead
    console.error('[FacebookEvents.initialize] Failed:', error);
  }
};
